import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthLegalFooter } from '../../auth/components/AuthLegalFooter';
import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from '../../auth/components/AuthPageLayout';
import { confirmSignUp, globalSignUp, savePickerSession } from '../services/workspaceService';
import type { SignUpResult } from '../types/workspace';
import styles from './WorkspacePicker.module.css';

const MINIMUM_PASSWORD_LENGTH = 8;

/**
 * Creates the Simaizeer account on the global app host. There is deliberately no workspace yet: the
 * new account goes on to create its first one, or to be invited into somebody else's.
 */
export const SignUpPage: React.FC = () => {
  const { t } = useTranslation(['workspaces', 'auth']);
  const navigate = useNavigate();
  const [displayName, setDisplayName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [pendingEmail, setPendingEmail] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const continueWithAccount = (result: SignUpResult) => {
    savePickerSession({
      token: '',
      expiresAt: result.accountExpiresAtUtc,
      username: result.account.email,
      workspaces: [],
      accountToken: result.accountToken,
    });

    navigate('/workspaces/new', { replace: true });
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t('auth:errors.usernameRequired'));
      return;
    }

    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      setError(t('workspaces:signUp.passwordTooShort', { count: MINIMUM_PASSWORD_LENGTH }));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await globalSignUp(email.trim(), password, displayName.trim());
      if (result.verificationRequired) {
        setPendingEmail(result.email ?? email.trim());
        setCode('');
        setIsSubmitting(false);
        return;
      }

      if (!result.account) {
        throw new Error(t('workspaces:errors.signUpFailed'));
      }

      continueWithAccount(result.account);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workspaces:errors.signUpFailed'));
      setIsSubmitting(false);
    }
  };

  const onConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pendingEmail) return;

    setError(null);
    if (!code.trim()) {
      setError(t('workspaces:signUp.codeRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      continueWithAccount(await confirmSignUp(pendingEmail, code.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workspaces:errors.signUpFailed'));
      setIsSubmitting(false);
    }
  };

  const onResend = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await globalSignUp(email.trim(), password, displayName.trim());
      setPendingEmail(result.email ?? email.trim());
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workspaces:errors.signUpFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pendingEmail) {
    return (
      <AuthPageLayout title={t('workspaces:signUp.verifyTitle')} subtitle={t('workspaces:signUp.verifySubtitle', { email: pendingEmail })} footer={<AuthLegalFooter />}>
        <form className={authStyles.form} onSubmit={onConfirm}>
          {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

          <AuthField label={t('workspaces:signUp.codeLabel')} htmlFor="signup-code" icon="mail">
            <AuthInput
              id="signup-code"
              value={code}
              onChange={event => setCode(event.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder={t('workspaces:signUp.codePlaceholder')}
            />
          </AuthField>

          <AuthSubmitButton loading={isSubmitting} loadingLabel={t('workspaces:signUp.verifying')}>
            {t('workspaces:signUp.verifySubmit')}
          </AuthSubmitButton>
        </form>

        <p className={styles.footer}>
          <button type="button" className={styles.linkButton} onClick={() => { void onResend(); }} disabled={isSubmitting}>
            {t('workspaces:signUp.resend')}
          </button>
          <button type="button" className={styles.linkButton} onClick={() => { setPendingEmail(null); setError(null); }}>
            {t('workspaces:signUp.changeEmail')}
          </button>
        </p>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout title={t('workspaces:signUp.title')} subtitle={t('workspaces:signUp.subtitle')} footer={<AuthLegalFooter />}>
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

        <AuthField label={t('workspaces:signUp.nameLabel')} htmlFor="signup-name" icon="mail">
          <AuthInput
            id="signup-name"
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            autoComplete="name"
            placeholder={t('workspaces:signUp.namePlaceholder')}
          />
        </AuthField>

        <AuthField label={t('auth:emailLabel')} htmlFor="signup-email" icon="mail">
          <AuthInput
            id="signup-email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            autoComplete="username"
            placeholder={t('auth:emailPlaceholder')}
          />
        </AuthField>

        <AuthField label={t('auth:passwordLabel')} htmlFor="signup-password" icon="lock">
          <AuthInput
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="new-password"
            placeholder={t('auth:passwordPlaceholder')}
            withToggle
            revealed={showPassword}
            onToggleReveal={() => setShowPassword(current => !current)}
            toggleLabel={showPassword ? t('auth:hidePassword') : t('auth:showPassword')}
          />
        </AuthField>

        <AuthSubmitButton loading={isSubmitting} loadingLabel={t('workspaces:signUp.submitting')}>
          {t('workspaces:signUp.submit')}
        </AuthSubmitButton>
      </form>

      <p className={styles.footer}>
        <span>{t('workspaces:signUp.haveAccount')}</span>
        <Link className={styles.linkButton} to="/login">
          {t('auth:signIn')}
        </Link>
      </p>
    </AuthPageLayout>
  );
};

export default SignUpPage;
